import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ImageTransformation,
  Price as PriceInterface,
  Product,
  ProductDetails as ProductDetailsInterface,
  ProductService,
  Variant,
} from '@services';
import { AnalyticsListContext, getImageTransformationByProductType, getSuccessfullyLoadedUrls } from '@utils';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { Badge, Carousel, Counter, Typography } from 'gd-design-library';
import { AddToCartButton, Button, Price, ProductOptions } from '@components';
import NoImagePlaceholder from '@assets/images/no-image-placeholder.png';
import { useServiceRef } from '../../hooks';
import { useRouter } from 'next/router';
import { ROUTES } from '@constants';
import classNames from 'classnames/bind';
import styles from './BestMatchProductCard.module.scss';

const cx = classNames.bind(styles);

const CN = 'best-match-product-card';
const badgeClassName = `${CN}__badge`;
const imageContainerClassName = `${CN}__image-container`;
const imageClassName = `${CN}__image`;
const carouselWrapperClassName = `${CN}__carousel-wrapper`;
const bodyClassName = `${CN}__body`;
const nameRowClassName = `${CN}__name-row`;
const detailsButtonClassName = `${CN}__details-button`;

const isCloudinaryUrl = (url?: string): boolean => !!url?.includes('cloudinary');

type Availability = Record<string, { values: Record<string, boolean> }>;

interface BestMatchProductCardProps {
  product: Product;
  handleAddToCart: (draft: LineItemDraft, currencyCode?: string) => Promise<void>;
  analyticsContext: AnalyticsListContext;
}

export const BestMatchProductCard: React.FC<BestMatchProductCardProps> = ({
  product: listProduct,
  handleAddToCart: onAddToCart
}) => {
  const { id: productId, matchingVariantId } = listProduct;

  const productServiceRef = useServiceRef(ProductService);
  const router = useRouter();

  const [ product, setProduct ] = useState<ProductDetailsInterface | null>(null);
  const [ selectedVariant, setSelectedVariant ] = useState<Variant>({});
  const [ quantityValue, setQuantityValue ] = useState(1);
  const [ isAddingToCart, setIsAddingToCart ] = useState(false);
  const [ successfullyLoadedImages, setSuccessfullyLoadedImages ] = useState<string[]>([]);
  const [ isLoadingImages, setIsLoadingImages ] = useState(true);
  const [ availableQuantity, setAvailableQuantity ] = useState<number | null>(null);
  const [ optionsAvailability, setOptionsAvailability ] = useState<Availability>({});

  useEffect(() => {
    let cancelled = false;
    productServiceRef.current.getProductData(productId).then(details => {
      if (!cancelled && details) {
        setProduct(details);
        const matchingVariant = matchingVariantId
          ? details.variants?.find(v => v.id === matchingVariantId)
          : undefined;
        const firstAvailable = details.variants?.find(v => v.isOnStock) ?? details.variants?.[0];
        const initial = matchingVariant ?? firstAvailable;
        if (initial) setSelectedVariant(initial);
      }
    });

    return (): void => { cancelled = true; };
  }, [ productId, matchingVariantId ]);

  const { attributes: selectedVariantAttributes, images: selectedVariantImages, price: selectedVariantPrice } = selectedVariant;

  const currentImages: string[] = useMemo(
    () => selectedVariantImages?.length ? selectedVariantImages : (product?.images ?? []),
    [ selectedVariantImages, product?.images ]
  );

  const currentPrice: PriceInterface | undefined = selectedVariantPrice?.value ? selectedVariantPrice : product?.price;

  const imageTransformation = new ImageTransformation();

  const productImageURLs = successfullyLoadedImages.length ?
    successfullyLoadedImages.map(image => isCloudinaryUrl(image)
      ? imageTransformation.transform(image, getImageTransformationByProductType(product?.productType ?? ''))
      : image) :
    [ NoImagePlaceholder.src ];

  const outOfStockOptions = useMemo(() => {
    const { options, variants } = product ?? {};
    if (!options || !variants || !selectedVariantAttributes) return {};

    const result: Record<string, Record<string, boolean>> = {};
    for (const option of options) {
      result[option.name] = {};
      if (option.values) {
        for (const value of option.values) {
          const attributes = { ...selectedVariantAttributes, [option.name]: value };
          const variant = variants.find(v =>
            Object.entries(attributes).every(([ k, vAttr ]) => v.attributes[k] === vAttr)
          );
          result[option.name][String(value)] = !variant?.isOnStock;
        }
      }
    }

    return result;
  }, [ product, selectedVariantAttributes ]);

  useEffect(() => {
    getSuccessfullyLoadedUrls(currentImages).then((urls: string[]) => {
      if (urls.length) setSuccessfullyLoadedImages(urls);
      setIsLoadingImages(false);
    });
  }, [ currentImages ]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (selectedVariant?.sku) {
        const quantity = await productServiceRef.current.getAvailableQuantity(selectedVariant.sku);
        setAvailableQuantity(quantity);
      } else {
        setAvailableQuantity(null);
      }
    })();
  }, [ selectedVariant?.sku ]);

  useEffect(() => {
    const { options, variants } = product ?? {};
    if (!options || !variants) return;

    const variantSet = variants.reduce((acc, v) => acc.add(JSON.stringify(v.attributes)), new Set<string>());
    const calculated: Availability = {};

    options.forEach(option => {
      calculated[option.name] = { values: {} };
      option.values?.forEach(value => {
        const newVariant = { ...selectedVariantAttributes, [option.name]: value };
        calculated[option.name].values[String(value)] = variantSet.has(JSON.stringify(newVariant));
      });
    });

    setOptionsAvailability(calculated);
  }, [ selectedVariantAttributes, product ]);

  const onClickOption = (optionName: string, optionValue: unknown): void => {
    const variants = product?.variants ?? [];
    const userSelectedVariant = { ...selectedVariantAttributes, [optionName]: optionValue };
    const found = variants.find(v => JSON.stringify(v.attributes) === JSON.stringify(userSelectedVariant));
    if (found) {
      setSelectedVariant({ ...found });
    } else {
      const fallback = variants.find(v => v.attributes[optionName] === optionValue);
      if (fallback) setSelectedVariant(fallback);
    }
  };

  const handleAddToCart = useCallback((): void => {
    if (!selectedVariant.id && !product?.variants?.[0]?.id) return;
    const variantId = Number(selectedVariant.id ?? product?.variants?.[0]?.id);
    const currencyCode = (selectedVariant.price ?? product?.price)?.currencyCode;

    setIsAddingToCart(true);
    onAddToCart({ productId, variantId, quantity: quantityValue }, currencyCode)
      .finally(() => setIsAddingToCart(false));
  }, [ productId, selectedVariant, product, quantityValue, onAddToCart ]);

  if (!product) return null;

  const { name, options = [] } = product;

  return (
    <div className={ cx(CN) }>
      <div className={ cx(badgeClassName) }>
        <Badge variant='primary' appearance='filled' size='sm'>Best Match</Badge>
      </div>
      <div className={ cx(imageContainerClassName) }>
        {!isLoadingImages && (
          productImageURLs.length === 1 ?
            <img src={ productImageURLs[0] } alt={ name } className={ cx(imageClassName) } /> :
            <div className={ cx(carouselWrapperClassName) }>
              <style>{'[data-testid="Carousel-thumbnails"] { display: none; }'}</style>
              <Carousel showArrows>
                {productImageURLs.map((src, i) => <Carousel.Image key={ i } src={ src } />)}
              </Carousel>
            </div>
        )}
      </div>
      <div className={ cx(bodyClassName) }>
        <div className={ cx(nameRowClassName) }>
          <Typography variant='h4'>{name}</Typography>
          <Price price={ selectedVariant.price ?? currentPrice } />
        </div>
        {options
          .filter(option => option.name !== 'sokos-sokos_product-purchase_limit')
          .map(currentOptions => (
            <ProductOptions
              key={ currentOptions.name }
              options={ currentOptions }
              selectedOption={ selectedVariantAttributes?.[currentOptions.name] }
              availability={ optionsAvailability[currentOptions.name]?.values }
              outOfStockOptions={ outOfStockOptions[currentOptions.name] }
              onClickOption={ onClickOption }
              variants={ product.variants }
            />
          ))}
        <Button
          variant='inherit'
          onClick={ (): void => { router.push(`${ROUTES.products}/${productId}`); } }
          className={ cx(detailsButtonClassName) }
        >
          See more details
        </Button>
        <Counter
          initial={ quantityValue }
          min={ 1 }
          max={ availableQuantity ?? (!selectedVariant.isOnStock ? 1 : undefined) }
          isDisabled={ !selectedVariant.isOnStock }
          onCounterChange={ setQuantityValue }
        />
        <AddToCartButton
          onClick={ handleAddToCart }
          isLoading={ isAddingToCart }
          disabled={ selectedVariant?.price?.value === undefined || currentPrice?.value === undefined }
          isOnStock={ selectedVariant.isOnStock }
        />
      </div>
    </div>
  );
};
